import React, { useState } from 'react';
import { colorwayFor } from '../colorways';
import { IconPlus, IconCheck, IconX, IconPencil, IconTrash, IconAlert, IconKey } from '../icons';

export default function AdminView({ children, spendingChoices, adminToken, setAdminToken, onRefresh, API, headingRef }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [editingChild, setEditingChild] = useState(null);
  const [editChildData, setEditChildData] = useState({});
  const [editingChoice, setEditingChoice] = useState(null);
  const [editChoiceData, setEditChoiceData] = useState({});
  const [confirming, setConfirming] = useState(null); // `child:3` | `choice:5`
  const [showAddChild, setShowAddChild] = useState(false);
  const [showAddChoice, setShowAddChoice] = useState(false);
  const [newChild, setNewChild] = useState({ name: '', tokens: '0' });
  const [newChoice, setNewChoice] = useState({ name: '', description: '', price: '' });
  const [saving, setSaving] = useState(false);

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${adminToken}`,
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'That did not work');
      setAdminToken(data.token);
    } catch (err) {
      setLoginError(
        err.message === 'Invalid credentials'
          ? 'That username and password do not match. Try again.'
          : err.message || 'Could not sign in. Is the server running?'
      );
    }
  };

  // --- children ---
  const saveChild = async (child) => {
    setSaving(true);
    try {
      await fetch(`${API}/children/${child.id}`, {
        method: 'PUT', headers: authHeaders, body: JSON.stringify(editChildData),
      });
      setEditingChild(null);
      onRefresh();
    } finally { setSaving(false); }
  };

  const deleteChild = async (id) => {
    await fetch(`${API}/children/${id}`, { method: 'DELETE', headers: authHeaders });
    setConfirming(null);
    onRefresh();
  };

  const addChild = async () => {
    if (!newChild.name.trim()) return;
    setSaving(true);
    try {
      await fetch(`${API}/children`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ name: newChild.name.trim(), tokens: parseInt(newChild.tokens, 10) || 0 }),
      });
      setNewChild({ name: '', tokens: '0' });
      setShowAddChild(false);
      onRefresh();
    } finally { setSaving(false); }
  };

  // --- rewards ---
  const saveChoice = async (choice) => {
    setSaving(true);
    try {
      await fetch(`${API}/spending-choices/${choice.id}`, {
        method: 'PUT', headers: authHeaders, body: JSON.stringify(editChoiceData),
      });
      setEditingChoice(null);
      onRefresh();
    } finally { setSaving(false); }
  };

  const deleteChoice = async (id) => {
    await fetch(`${API}/spending-choices/${id}`, { method: 'DELETE', headers: authHeaders });
    setConfirming(null);
    onRefresh();
  };

  const addChoice = async () => {
    if (!newChoice.name.trim() || !newChoice.price) return;
    setSaving(true);
    try {
      await fetch(`${API}/spending-choices`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          name: newChoice.name.trim(),
          description: newChoice.description.trim(),
          price: parseInt(newChoice.price, 10),
        }),
      });
      setNewChoice({ name: '', description: '', price: '' });
      setShowAddChoice(false);
      onRefresh();
    } finally { setSaving(false); }
  };

  if (!adminToken) {
    return (
      <div className="admin-view">
        <div className="sheet login">
          <h1 tabIndex={-1} ref={headingRef}>Grown-ups only</h1>
          <p>
            Sign in to add children, put rewards on the shelf, or set a balance by hand.
            Adding and spending tokens does not need a sign-in.
          </p>
          {loginError && <p className="note note--bad" style={{ marginBottom: '1rem' }}><IconAlert />{loginError}</p>}
          <form onSubmit={handleLogin}>
            <div className="field">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                className="input"
                type="text"
                autoComplete="username"
                value={username}
                onChange={e => setUsername(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                className="input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn--primary btn--block">
              <IconKey />
              Sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-view">
      <header className="page-head">
        <h1 tabIndex={-1} ref={headingRef}>The Stockroom</h1>
        <p>Add children, price the rewards, and correct a balance when it drifts.</p>
      </header>

      {/* ---- Children ---- */}
      <section className="section">
        <div className="section-head">
          <h2>
            Children
            <span className="section-count">{children.length} on the wall</span>
          </h2>
          <button
            type="button"
            className={showAddChild ? 'btn btn--ghost' : 'btn btn--primary'}
            onClick={() => setShowAddChild(v => !v)}
          >
            {showAddChild ? <IconX /> : <IconPlus />}
            {showAddChild ? 'Cancel' : 'Add child'}
          </button>
        </div>

        {showAddChild && (
          <div className="drawer">
            <label htmlFor="new-child-name">New child</label>
            <div className="field-row">
              <input
                id="new-child-name"
                className="input"
                placeholder="Name"
                value={newChild.name}
                onChange={e => setNewChild({ ...newChild, name: e.target.value })}
              />
              <input
                className="input input--num"
                type="number"
                inputMode="numeric"
                aria-label="Starting tokens"
                placeholder="0"
                value={newChild.tokens}
                onChange={e => setNewChild({ ...newChild, tokens: e.target.value })}
              />
            </div>
            <button type="button" className="btn btn--primary" onClick={addChild} disabled={saving || !newChild.name.trim()}>
              <IconCheck />
              {saving ? 'Saving…' : 'Put the box up'}
            </button>
          </div>
        )}

        <div className="rows">
          {children.map(child => {
            const { primary } = colorwayFor(child.id);
            const isConfirming = confirming === `child:${child.id}`;
            return (
              <div key={child.id} className={`row${isConfirming ? ' row--danger' : ''}`}>
                <span className="row-swatch" style={{ background: primary.hex }} aria-hidden="true" />

                {editingChild === child.id ? (
                  <div className="row-edit">
                    <div className="field-row">
                      <input
                        className="input"
                        aria-label={`Name for ${child.name}`}
                        value={editChildData.name}
                        onChange={e => setEditChildData({ ...editChildData, name: e.target.value })}
                      />
                      <input
                        className="input input--num"
                        type="number"
                        inputMode="numeric"
                        aria-label={`Token balance for ${child.name}`}
                        value={editChildData.tokens}
                        onChange={e => setEditChildData({ ...editChildData, tokens: parseInt(e.target.value, 10) || 0 })}
                      />
                    </div>
                    <div className="row-actions">
                      <button type="button" className="btn-icon" onClick={() => saveChild(child)} disabled={saving} aria-label={`Save changes to ${child.name}`}>
                        <IconCheck />
                      </button>
                      <button type="button" className="btn-icon" onClick={() => setEditingChild(null)} aria-label="Cancel editing">
                        <IconX />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="row-main">
                      <div className="row-name">{child.name}</div>
                    </div>
                    <div className="row-fig">
                      {child.tokens}
                      <span className="sr-only"> tokens</span>
                    </div>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="btn-icon"
                        aria-label={`Edit ${child.name}`}
                        onClick={() => {
                          setEditingChild(child.id);
                          setConfirming(null);
                          setEditChildData({ name: child.name, tokens: child.tokens });
                        }}
                      >
                        <IconPencil />
                      </button>
                      <button
                        type="button"
                        className="btn-icon btn-icon--danger"
                        aria-label={`Remove ${child.name}`}
                        onClick={() => setConfirming(isConfirming ? null : `child:${child.id}`)}
                      >
                        <IconTrash />
                      </button>
                    </div>
                  </>
                )}

                {isConfirming && (
                  <div className="confirm">
                    <p>
                      Take {child.name}&rsquo;s box off the wall? Their {child.tokens}{' '}
                      {child.tokens === 1 ? 'token' : 'tokens'} and their whole history go with it.
                      This cannot be undone.
                    </p>
                    <button type="button" className="btn btn--danger" onClick={() => deleteChild(child.id)}>
                      <IconTrash />
                      Remove {child.name}
                    </button>
                    <button type="button" className="btn btn--ghost" onClick={() => setConfirming(null)}>
                      Keep
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ---- Rewards ---- */}
      <section className="section">
        <div className="section-head">
          <h2>
            Rewards
            <span className="section-count">{spendingChoices.length} on the shelf</span>
          </h2>
          <button
            type="button"
            className={showAddChoice ? 'btn btn--ghost' : 'btn btn--primary'}
            onClick={() => setShowAddChoice(v => !v)}
          >
            {showAddChoice ? <IconX /> : <IconPlus />}
            {showAddChoice ? 'Cancel' : 'Add reward'}
          </button>
        </div>

        {showAddChoice && (
          <div className="drawer">
            <label htmlFor="new-reward-name">New reward</label>
            <div className="field-row">
              <input
                id="new-reward-name"
                className="input"
                placeholder="Name"
                value={newChoice.name}
                onChange={e => setNewChoice({ ...newChoice, name: e.target.value })}
              />
              <input
                className="input input--num"
                type="number"
                inputMode="numeric"
                min="1"
                aria-label="Price in tokens"
                placeholder="0"
                value={newChoice.price}
                onChange={e => setNewChoice({ ...newChoice, price: e.target.value })}
              />
            </div>
            <input
              className="input"
              aria-label="Description (optional)"
              placeholder="Description (optional)"
              value={newChoice.description}
              onChange={e => setNewChoice({ ...newChoice, description: e.target.value })}
            />
            <button
              type="button"
              className="btn btn--primary"
              onClick={addChoice}
              disabled={saving || !newChoice.name.trim() || !newChoice.price}
            >
              <IconCheck />
              {saving ? 'Saving…' : 'Put it on the shelf'}
            </button>
          </div>
        )}

        <div className="rows">
          {spendingChoices.map(choice => {
            const isConfirming = confirming === `choice:${choice.id}`;
            return (
              <div key={choice.id} className={`row${isConfirming ? ' row--danger' : ''}`}>
                {editingChoice === choice.id ? (
                  <div className="row-edit">
                    <div className="field-row">
                      <input
                        className="input"
                        aria-label={`Name for ${choice.name}`}
                        value={editChoiceData.name}
                        onChange={e => setEditChoiceData({ ...editChoiceData, name: e.target.value })}
                      />
                      <input
                        className="input input--num"
                        type="number"
                        inputMode="numeric"
                        aria-label={`Price for ${choice.name}`}
                        value={editChoiceData.price}
                        onChange={e => setEditChoiceData({ ...editChoiceData, price: parseInt(e.target.value, 10) || 0 })}
                      />
                    </div>
                    <input
                      className="input"
                      aria-label={`Description for ${choice.name}`}
                      placeholder="Description (optional)"
                      value={editChoiceData.description}
                      onChange={e => setEditChoiceData({ ...editChoiceData, description: e.target.value })}
                    />
                    <div className="row-actions">
                      <button type="button" className="btn-icon" onClick={() => saveChoice(choice)} disabled={saving} aria-label={`Save changes to ${choice.name}`}>
                        <IconCheck />
                      </button>
                      <button type="button" className="btn-icon" onClick={() => setEditingChoice(null)} aria-label="Cancel editing">
                        <IconX />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="row-main">
                      <div className="row-name">{choice.name}</div>
                      {choice.description && <div className="row-desc">{choice.description}</div>}
                    </div>
                    <div className="row-fig">
                      {choice.price}
                      <span className="sr-only"> tokens</span>
                    </div>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="btn-icon"
                        aria-label={`Edit ${choice.name}`}
                        onClick={() => {
                          setEditingChoice(choice.id);
                          setConfirming(null);
                          setEditChoiceData({
                            name: choice.name,
                            description: choice.description || '',
                            price: choice.price,
                          });
                        }}
                      >
                        <IconPencil />
                      </button>
                      <button
                        type="button"
                        className="btn-icon btn-icon--danger"
                        aria-label={`Remove ${choice.name}`}
                        onClick={() => setConfirming(isConfirming ? null : `choice:${choice.id}`)}
                      >
                        <IconTrash />
                      </button>
                    </div>
                  </>
                )}

                {isConfirming && (
                  <div className="confirm">
                    <p>
                      Take &ldquo;{choice.name}&rdquo; off the shelf? Nobody will be able to
                      spend on it again. Tokens already spent on it stay spent.
                    </p>
                    <button type="button" className="btn btn--danger" onClick={() => deleteChoice(choice.id)}>
                      <IconTrash />
                      Remove it
                    </button>
                    <button type="button" className="btn btn--ghost" onClick={() => setConfirming(null)}>
                      Keep
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
